import { supabase, MODEL_FOR_PLAN } from "./supabase.js";

// UI plan selection → storage {plan_tier, ai_model}
// Pay screen radios are 'once'|'basic'|'pro'.  it_cases.plan_tier only
// allows 'trial'|'basic'|'pro', so a one-time purchase is stored as 'trial'.
export function mapPlanUIToCase(uiPlan) {
  const map = {
    once: { plan_tier: "trial", ai_model: MODEL_FOR_PLAN.trial },
    basic: { plan_tier: "basic", ai_model: MODEL_FOR_PLAN.basic },
    pro: { plan_tier: "pro", ai_model: MODEL_FOR_PLAN.pro },
  };
  return map[uiPlan] ?? map.once;
}

// Find an unsubmitted draft case for this child, or create a fresh one.
export async function findOrCreateDraftCase({ userId, childId, planTier = "trial" }) {
  const { data: existing, error: findErr } = await supabase
    .from("it_cases")
    .select("*")
    .eq("parent_id", userId)
    .eq("child_id", childId)
    .eq("status", "draft")
    .order("created_at", { ascending: false })
    .limit(1);

  if (findErr) throw findErr;
  if (existing && existing.length > 0) return existing[0];

  const aiModel = MODEL_FOR_PLAN[planTier] ?? MODEL_FOR_PLAN.trial;
  const { data, error } = await supabase
    .from("it_cases")
    .insert({
      parent_id: userId,
      child_id: childId,
      status: "draft",
      plan_tier: planTier,
      ai_model: aiModel,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateCaseConcern({ caseId, tags, memo }) {
  const { error } = await supabase
    .from("it_cases")
    .update({ concern_tags: tags, concern_memo: memo })
    .eq("id", caseId);
  if (error) throw error;
}

export async function getCaseVideos(caseId) {
  const { data, error } = await supabase
    .from("it_videos")
    .select("id, position, duration_seconds, file_size_bytes, storage_path, mime_type, thumbnail_path")
    .eq("case_id", caseId)
    .order("position");
  if (error) throw error;
  return data ?? [];
}

// 모바일/데스크톱 판별. 두 곳에서 쓰인다.
//  1) probeVideoMeta에서 큰 파일 캡처 스킵(모바일 OOM 방지)
//  2) PDF 저장 시 Web Share sheet 호출 여부 (PC는 그냥 다운로드)
export function isMobileLike() {
  if (typeof navigator === "undefined") return false;
  if (typeof navigator.userAgentData?.mobile === "boolean") {
    return navigator.userAgentData.mobile;
  }
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}
const THUMB_CAPTURE_LIMIT_BYTES = isMobileLike()
  ? 60 * 1024 * 1024
  : 500 * 1024 * 1024;

// Perceived-luminance check so we don't store a black intro frame as the
// thumbnail. Phone videos often fade in / auto-expose for the first ~1s, so
// the very first frame is frequently pure black even when the clip has content.
// getImageData throws on a CORS-tainted canvas — there we can't measure, so we
// treat the frame as "not dark" and accept it.
function frameIsTooDark(ctx, w, h) {
  try {
    const { data } = ctx.getImageData(0, 0, w, h);
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    return sum / (data.length / 4) < 18; // 0..255 — <18 is essentially black
  } catch {
    return false;
  }
}

// Candidate capture timestamps in priority order. We deliberately skip the very
// start (often black) and only fall back to it as a last resort.
function pickThumbTimes(duration) {
  if (Number.isFinite(duration) && duration > 0) {
    const cap = Math.max(duration - 0.1, 0.1);
    return [
      Math.min(Math.max(duration * 0.1, 1.0), cap),
      Math.min(duration * 0.4, cap),
      Math.min(duration * 0.7, cap),
      0.1,
    ];
  }
  return [1.0, 2.5, 0.3];
}

// Probe duration + grab a frame thumbnail from a hidden <video>.
// Mobile recordings often have the moov atom at the end of the file, so
// "preload=metadata" can scan the entire 100MB+ file and OOM-kill the WebView
// tab on Android. We cap the whole operation at 8s and skip thumbnail extraction
// for files >60MB to keep memory low. Either field may resolve as null — the
// caller treats both as optional.
//
// To avoid black thumbnails we seek a little way into the clip (see
// pickThumbTimes) and, if the captured frame is still essentially black, retry
// at a later timestamp before giving up (see frameIsTooDark).
function probeVideoMeta(file) {
  return new Promise((resolve) => {
    const out = { duration: null, thumbnail: null };
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "auto"; // need real frame data, not just metadata
    v.muted = true;
    v.playsInline = true;
    // Visible-but-tiny: opacity:0 or width:1px can make some Android browsers
    // skip the video pipeline entirely. opacity 0.01 + 4×4px keeps the
    // compositor running so frames actually decode.
    v.style.cssText =
      "position:fixed;left:0;top:0;width:4px;height:4px;opacity:0.01;" +
      "pointer-events:none;z-index:-9999;";
    if (typeof document !== "undefined" && document.body) {
      document.body.appendChild(v);
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      try { URL.revokeObjectURL(url); } catch { /* noop */ }
      try { v.pause?.(); } catch { /* noop */ }
      try { v.removeAttribute("src"); v.load?.(); } catch { /* noop */ }
      try { v.remove(); } catch { /* noop */ }
      resolve(out);
    };

    let times = [];
    let idx = 0;

    // Seek to times[idx], wait for the next composited frame, then draw it.
    // `seeked`/`loadeddata` alone don't guarantee paint on mobile, so we race
    // rVFC against rAF×2 + a short timer — one of them always wakes us up.
    const grabAt = () => {
      if (settled) return;
      if (idx >= times.length) { finish(); return; }
      const onFrameReady = () => {
        let fired = false;
        const fire = () => {
          if (fired || settled) return;
          fired = true;
          if (!v.videoWidth || !v.videoHeight) { finish(); return; }
          const w = 180;
          const h = Math.round(w * (v.videoHeight / v.videoWidth));
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) { finish(); return; }
          ctx.drawImage(v, 0, 0, w, h);
          const lastTry = idx >= times.length - 1;
          if (!lastTry && frameIsTooDark(ctx, w, h)) {
            idx += 1;
            grabAt(); // black intro — try a later timestamp
            return;
          }
          out.thumbnail = canvas.toDataURL("image/jpeg", 0.6);
          finish();
        };
        if (typeof v.requestVideoFrameCallback === "function") {
          try { v.requestVideoFrameCallback(fire); } catch { /* noop */ }
        }
        requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(fire, 80)));
      };
      v.onseeked = onFrameReady;
      try { v.currentTime = times[idx]; } catch { finish(); }
    };

    v.onloadedmetadata = () => {
      out.duration = Number.isFinite(v.duration) ? Math.round(v.duration) : null;
    };

    v.onloadeddata = () => {
      // Skip frame extraction for very large files to avoid mobile OOM
      if (file.size > THUMB_CAPTURE_LIMIT_BYTES) { finish(); return; }
      times = pickThumbTimes(out.duration);
      idx = 0;
      // Prime the decoder on mobile (muted+playsInline autoplay needs no
      // gesture), then seek+grab. If autoplay is blocked we still seek.
      const playPromise = v.play?.();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => { try { v.pause(); } catch { /* noop */ } grabAt(); })
          .catch(() => { grabAt(); });
      } else {
        grabAt();
      }
    };

    v.onerror = finish;
    setTimeout(finish, 8000);
    v.src = url;
  });
}

const THUMB_PREFIX = "it:thumb:";
function saveThumb(storagePath, dataUrl) {
  if (!dataUrl || typeof window === "undefined") return;
  try { window.localStorage.setItem(THUMB_PREFIX + storagePath, dataUrl); }
  catch { /* quota exceeded — ignore */ }
}
function clearThumb(storagePath) {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(THUMB_PREFIX + storagePath); } catch { /* noop */ }
}
export function getThumb(storagePath) {
  if (!storagePath || typeof window === "undefined") return null;
  try { return window.localStorage.getItem(THUMB_PREFIX + storagePath); }
  catch { return null; }
}

// Cross-device fallback: when the local capture is missing (e.g. uploaded
// from another device, or a desktop file too large to decode), fetch the
// .thumb.jpg that was uploaded alongside the video as a signed URL.
export async function getRemoteThumbUrl(thumbnailPath, expiresIn = 3600) {
  if (!thumbnailPath) return null;
  const { data, error } = await supabase.storage
    .from("it-videos")
    .createSignedUrl(thumbnailPath, expiresIn);
  if (error) return null;
  return data?.signedUrl ?? null;
}

async function dataUrlToBlob(dataUrl) {
  if (!dataUrl) return null;
  try {
    const res = await fetch(dataUrl);
    return await res.blob();
  } catch { return null; }
}

// Safety net for cases where probeVideoMeta failed (PC autoplay quirks,
// codec not decodable from File object, etc.). Pulls the already-uploaded
// video back via a signed URL and tries to extract a frame from there.
function extractThumbnailFromUrl(url) {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    v.crossOrigin = "anonymous";
    v.preload = "auto";
    v.muted = true;
    v.playsInline = true;
    v.style.cssText =
      "position:fixed;left:0;top:0;width:4px;height:4px;opacity:0.01;" +
      "pointer-events:none;z-index:-9999;";
    if (typeof document !== "undefined" && document.body) {
      document.body.appendChild(v);
    }
    let settled = false;
    let result = null;
    const finish = () => {
      if (settled) return;
      settled = true;
      try { v.pause?.(); } catch { /* noop */ }
      try { v.removeAttribute("src"); v.load?.(); } catch { /* noop */ }
      try { v.remove(); } catch { /* noop */ }
      resolve(result);
    };

    let times = [];
    let idx = 0;
    const grabAt = () => {
      if (settled) return;
      if (idx >= times.length) { finish(); return; }
      const onFrameReady = () => {
        let fired = false;
        const fire = () => {
          if (fired || settled) return;
          fired = true;
          if (!v.videoWidth || !v.videoHeight) { finish(); return; }
          const w = 240;
          const h = Math.round(w * (v.videoHeight / v.videoWidth));
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) { finish(); return; }
          ctx.drawImage(v, 0, 0, w, h);
          const lastTry = idx >= times.length - 1;
          if (!lastTry && frameIsTooDark(ctx, w, h)) {
            idx += 1;
            grabAt();
            return;
          }
          result = canvas.toDataURL("image/jpeg", 0.7);
          finish();
        };
        if (typeof v.requestVideoFrameCallback === "function") {
          try { v.requestVideoFrameCallback(fire); } catch { /* noop */ }
        }
        requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(fire, 80)));
      };
      v.onseeked = onFrameReady;
      try { v.currentTime = times[idx]; } catch { finish(); }
    };

    v.onloadeddata = () => {
      times = pickThumbTimes(Number.isFinite(v.duration) ? v.duration : null);
      idx = 0;
      const p = v.play?.();
      if (p && typeof p.then === "function") {
        p.then(() => { try { v.pause(); } catch { /* noop */ } grabAt(); })
          .catch(() => { grabAt(); });
      } else {
        grabAt();
      }
    };
    v.onerror = finish;
    setTimeout(finish, 14000);
    v.src = url;
  });
}

const MAX_DURATION_SECONDS = 65; // 1분 + 5초 여유
const MAX_FILE_BYTES = 110 * 1024 * 1024; // it-videos 버킷 제한과 일치 (마이그레이션 0003)

// Some Android pickers report an empty or generic file.type (""/octet-stream)
// even for real videos, and some report a MIME the bucket allowlist rejects.
// We infer a sane content type from the extension so the upload still sends a
// type the bucket accepts. Keep this in sync with allowed_mime_types
// (migrations 0003 / 0012 / 0015).
const EXT_TO_VIDEO_MIME = {
  mp4: "video/mp4",
  m4v: "video/x-m4v",
  mov: "video/quicktime",
  qt: "video/quicktime",
  webm: "video/webm",
  "3gp": "video/3gpp",
  "3gpp": "video/3gpp",
  "3g2": "video/3gpp2",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  mpeg: "video/mpeg",
  mpg: "video/mpeg",
  ogv: "video/ogg",
};

// Resolve the content type to send to Storage. Prefer a real video/* MIME from
// the File; otherwise fall back to the extension. Returns null when we can't
// tell it's a video at all (so the caller can reject with a clear message).
function resolveVideoContentType(file, ext) {
  if (file.type && file.type.startsWith("video/")) return file.type;
  return EXT_TO_VIDEO_MIME[ext] ?? null;
}

export async function uploadCaseVideo({ caseId, userId, position, file }) {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("파일이 너무 커요 (최대 110MB)");
  }

  const rawExt = (file.name.split(".").pop() || "mp4").toLowerCase();
  const ext = rawExt.length <= 5 ? rawExt : "mp4";
  const contentType = resolveVideoContentType(file, ext);
  if (!contentType) {
    throw new Error("영상 파일만 업로드할 수 있어요");
  }

  const meta = await probeVideoMeta(file);
  const duration = meta.duration;
  if (duration != null && duration > MAX_DURATION_SECONDS) {
    throw new Error("영상은 1분 이하여야 해요");
  }

  const storagePath = `${userId}/${caseId}/${position}.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from("it-videos")
    .upload(storagePath, file, {
      cacheControl: "3600",
      upsert: true,
      contentType,
    });
  if (uploadErr) {
    // Surface the two failures parents actually hit with actionable text
    // instead of the raw Storage error ("업로드에 실패했어요").
    const msg = uploadErr.message || "";
    if (/mime type|not supported/i.test(msg)) {
      throw new Error("이 영상 형식은 지원하지 않아요. 가능하면 mp4로 올려주세요.");
    }
    if (/maximum allowed size|payload too large|413|exceeded/i.test(msg)) {
      throw new Error("파일이 너무 커요 (최대 110MB)");
    }
    throw uploadErr;
  }

  // Best-effort thumbnail upload (so admin can see it cross-device). Failure
  // here must NOT block the video row insert — the row is the source of truth.
  let thumbnailPath = null;
  if (meta.thumbnail) {
    const blob = await dataUrlToBlob(meta.thumbnail);
    if (blob) {
      const tPath = `${storagePath}.thumb.jpg`;
      const { error: tErr } = await supabase.storage
        .from("it-videos")
        .upload(tPath, blob, {
          cacheControl: "86400",
          upsert: true,
          contentType: "image/jpeg",
        });
      if (!tErr) thumbnailPath = tPath;
    }
  }

  const { data, error: insertErr } = await supabase
    .from("it_videos")
    .upsert(
      {
        case_id: caseId,
        position,
        storage_path: storagePath,
        duration_seconds: duration,
        file_size_bytes: file.size,
        mime_type: file.type,
        thumbnail_path: thumbnailPath,
      },
      { onConflict: "case_id,position" }
    )
    .select()
    .single();

  if (insertErr) throw insertErr;
  saveThumb(storagePath, meta.thumbnail);

  // Local capture failed (PC autoplay quirks, codec issues, …): pull the
  // just-uploaded video back through a signed URL and try once more. This
  // is best-effort — failure leaves the row as-is and the slot falls back
  // to the gradient placeholder.
  if (!thumbnailPath) {
    try {
      const { data: signed } = await supabase.storage
        .from("it-videos")
        .createSignedUrl(storagePath, 600);
      if (signed?.signedUrl) {
        const dataUrl = await extractThumbnailFromUrl(signed.signedUrl);
        if (dataUrl) {
          const blob = await dataUrlToBlob(dataUrl);
          if (blob) {
            const tPath = `${storagePath}.thumb.jpg`;
            const { error: tErr } = await supabase.storage
              .from("it-videos")
              .upload(tPath, blob, {
                cacheControl: "86400",
                upsert: true,
                contentType: "image/jpeg",
              });
            if (!tErr) {
              const { data: patched } = await supabase
                .from("it_videos")
                .update({ thumbnail_path: tPath })
                .eq("id", data.id)
                .select()
                .single();
              saveThumb(storagePath, dataUrl);
              if (patched) return patched;
            }
          }
        }
      }
    } catch { /* leave row without thumbnail_path */ }
  }

  return data;
}

export async function deleteCaseVideo({ videoId, storagePath }) {
  if (storagePath) {
    // Storage `.remove()` silently ignores missing paths, so it's safe to
    // include the thumbnail unconditionally.
    await supabase.storage
      .from("it-videos")
      .remove([storagePath, `${storagePath}.thumb.jpg`]);
    clearThumb(storagePath);
  }
  const { error } = await supabase.from("it_videos").delete().eq("id", videoId);
  if (error) throw error;
}

export async function updateChildProfile({ childId, name, birthMonth }) {
  const patch = {};
  if (name != null) patch.name = name;
  if (birthMonth !== undefined) patch.birth_month = birthMonth || null;
  if (Object.keys(patch).length === 0) return;
  const { error } = await supabase
    .from("it_children")
    .update(patch)
    .eq("id", childId);
  if (error) throw error;
}

export async function getCaseById(caseId) {
  const { data, error } = await supabase
    .from("it_cases")
    .select("*, it_children ( name )")
    .eq("id", caseId)
    .maybeSingle();
  if (error) throw error;
  return data;
}
